/**
 * Keyboard shortcut help overlay for the calendar arche.
 *
 * Displayed when the user presses `?`.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface ShortcutHelpOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutEntry {
  keys: string[];
  description: string;
}

const shortcuts: { section: string; entries: ShortcutEntry[] }[] = [
  {
    section: 'Navigation',
    entries: [
      { keys: ['←'], description: 'Previous period' },
      { keys: ['→'], description: 'Next period' },
      { keys: ['T'], description: 'Go to today' },
    ],
  },
  {
    section: 'Views',
    entries: [
      { keys: ['D'], description: 'Day view' },
      { keys: ['W'], description: 'Week view' },
      { keys: ['M'], description: 'Month view' },
      { keys: ['A'], description: 'Agenda view' },
    ],
  },
  {
    section: 'Actions',
    entries: [
      { keys: ['N'], description: 'New event' },
      { keys: ['/', '⌘K'], description: 'Command palette' },
      { keys: ['?'], description: 'Show this help' },
      { keys: ['Esc'], description: 'Close dialog' },
    ],
  },
];

export function ShortcutHelpOverlay({ open, onClose }: ShortcutHelpOverlayProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {shortcuts.map((group) => (
            <div key={group.section}>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {group.section}
              </h4>
              <div className="space-y-1">
                {group.entries.map((entry) => (
                  <div key={entry.description} className="flex items-center justify-between py-1">
                    <span className="text-sm">{entry.description}</span>
                    <span className="flex items-center gap-1">
                      {entry.keys.map((key, i) => (
                        <span key={i}>
                          {i > 0 && <span className="text-xs text-muted-foreground mx-0.5">/</span>}
                          <kbd className="px-1.5 py-0.5 bg-muted border rounded text-xs font-mono">
                            {key}
                          </kbd>
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
